import { I_WHITE_LOGO } from '../types/images';
import { useNavigate } from 'react-router-dom';
import {
    clearCenterViewSession,
    getCenterViewSession,
    isCenterViewTab,
    setItemWithExpiration,
} from '../lib/utils';
import { clearServerCache } from '../lib/queryClient';
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { requestSignInAPI } from '../api/auth';
import { endCenterViewSessionAPI, requestSuperAdminSignInAPI } from '../api/superAdminAuth';
import BusinessInfoFooter from '../components/BusinessInfoFooter';

const SignInPage = ({ createAlert }: any) => {
    const navigate = useNavigate();
    const [userId, setUserId] = useState('');
    const [userPassword, setUserPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);

    const leaveCenterViewTabForLogin = async () => {
        if (!isCenterViewTab()) return;

        const centerViewSession = getCenterViewSession();
        try {
            const viewSessionId = centerViewSession?.authObject?.viewSessionId;
            if (viewSessionId && centerViewSession?.authorization) {
                await endCenterViewSessionAPI(
                    viewSessionId,
                    centerViewSession.authorization,
                    'explicit',
                );
            }
        } catch {
            // 서버 종료 기록 실패 여부와 관계없이 명시적인 새 로그인은 계속 진행한다.
        } finally {
            clearCenterViewSession(true);
        }
    };

    // 일반 관리자 로그인
    const requestSignIn = useMutation({
        mutationFn: async () => await requestSignInAPI(userId, userPassword),
        onSuccess: async (res) => {
            if (res.status === 200) {
                await leaveCenterViewTabForLogin();
                const accessToken: string = res.data?.tokens?.access?.token;
                const user: any = res.data?.user;
                const center: any = res.data?.center;
                const subscriptionData: any = res.data?.subscriptionData;

                const authObject = {
                    adminId: user?.adminId,
                    id: user?.id,
                    name: user?.name,
                    email: user?.email,
                    gender: user?.gender,
                    phoneNumber: user?.phoneNumber,
                    profileImageUrl: user?.profileImageUrl,
                    centerAddress: center?.address,
                    centerCategory: center?.category,
                    centerCode: center?.code,
                    centerName: center?.name,
                    centerId: center?.id,
                    settlementType: center?.settlementType,
                    isNewCommunicationBox: res.data?.isNewCommunicationBox,
                    coachCount: subscriptionData?.coachCount,
                    memberCount: subscriptionData?.memberCount,
                    activationDate: subscriptionData?.activationDate,
                    expirationDate: subscriptionData?.expirationDate,
                    expirationDelayDate: subscriptionData?.expirationDelayDate,
                    finalPaymentDate: subscriptionData?.finalPaymentDate,
                    finalPaymentTryDate: subscriptionData?.finalPaymentTryDate,
                    cardIssuingBank: subscriptionData?.cardIssuingBank,
                    cardNumber: subscriptionData?.cardNumber,
                    planAmount: subscriptionData?.planAmount,
                    usableStatus: subscriptionData?.usableStatus,
                    paymentDueDate: null,
                    planLimitCount: subscriptionData?.planLimitCount,
                    prevPlanLimitCount: subscriptionData?.prevPlanLimitCount,
                    changedPlanLimitCount: subscriptionData?.changedPlanLimitCount,
                    confirmUsableStatus: false,
                    testAccount: false,
                    isSettingPayrolPassword: user?.isSettingPayrolPassword,

                    /** 부가세 정산 정책 */
                    vatPolicy: center?.vatPolicy ?? 'inclusive',
                    vatRate: center?.vatRate ?? 10,
                };

                setItemWithExpiration('authorization', accessToken, 12);
                setItemWithExpiration('authObject', authObject, 12);
                /*
                 * 🔴 새 세션은 **빈 캐시**에서 시작한다 (2026-09-03 대표 제보).
                 *
                 * 로그인은 페이지를 다시 띄우지 않으므로, 여기서 안 비우면 앞
                 * 계정이 남긴 응답 위에 새 세션이 얹힌다. centerId 가 없는 키는
                 * 그대로 재사용되어 **직전 센터의 강사 목록**이 보인다.
                 *
                 * 로그아웃 쪽(App.tsx)에서도 비우지만 여기서도 비운다 —
                 * 세션 만료·뒤로가기 등 로그아웃을 거치지 않고 이 화면에 닿는
                 * 길이 여러 개다. **계정이 바뀌는 지점은 결국 여기 하나다.**
                 */
                clearServerCache();
                navigate('/dash', { replace: true });
            }
            if (res.status === 400) {
                createAlert('', '아이디 비밀번호를 입력해주세요', true);
            }
            if (res.status === 404) {
                createAlert('존재하지 않은 아이디 입니다!', '입력하신 아이디를 다시 확인해주세요!', true);
            }
            if (res.status === 418) {
                createAlert(
                    '비밀번호가 일치하지 않습니다!',
                    '입력하신 비밀번호를 다시 확인해주세요!\n비밀번호를 잊으셨다면, 1:1 카톡채널 상담에 문의해주세요!',
                    true,
                );
            }
        },
    });

    // 슈퍼관리자 로그인
    const requestSuperAdminSignIn = useMutation({
        mutationFn: async () => await requestSuperAdminSignInAPI(userId, userPassword),
        onSuccess: async (res) => {
            if (res.status === 200) {
                await leaveCenterViewTabForLogin();
                const accessToken: string = res.data?.tokens?.access?.token;
                const user: any = res.data?.user;
                const centers: any[] = res.data?.centers || [];

                const authObject = {
                    role: 'superadmin',
                    superAdminId: user?.superAdminId,
                    id: user?.id,
                    name: user?.name,
                    email: user?.email,
                    phoneNumber: user?.phoneNumber,
                    profileImageUrl: user?.profileImageUrl,
                    brandName: user?.brandName,
                    centers,
                };

                setItemWithExpiration('authorization', accessToken, 12);
                setItemWithExpiration('authObject', authObject, 12);
                /* 슈퍼관리자도 같다 — 앞 계정의 캐시를 물려받지 않는다 */
                clearServerCache();
                navigate('/superadmin', { replace: true });
            }
            if (res.status === 400) {
                createAlert('', '아이디 비밀번호를 입력해주세요', true);
            }
            if (res.status === 404) {
                createAlert(
                    '등록되지 않은 슈퍼 관리자 계정입니다',
                    '슈퍼 관리자 이용을 원하신다면\n라포 카카오 채널로 문의주세요',
                    true,
                    '닫기',
                    undefined,
                    true,
                    '문의하기',
                    () => () => window.open('http://pf.kakao.com/_ICxhcb/chat', '_blank'),
                );
            }
            if (res.status === 418) {
                createAlert(
                    '비밀번호가 일치하지 않습니다!',
                    '입력하신 비밀번호를 다시 확인해주세요!\n비밀번호를 잊으셨다면, 1:1 카톡채널 상담에 문의해주세요!',
                    true,
                );
            }
        },
    });

    const handleLogin = () => {
        if (isSuperAdmin) {
            requestSuperAdminSignIn.mutate();
        } else {
            requestSignIn.mutate();
        }
    };

    const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && userPassword.length !== 0) {
            handleLogin();
        }
    };

    const isLoading = requestSignIn.isLoading || requestSuperAdminSignIn.isLoading;
    const isDisabled = userId.length === 0 || userPassword.length === 0 || isLoading;

    // 테마 색상
    const accent = isSuperAdmin ? '#1A305D' : '#4A7FE5';
    const accentHover = isSuperAdmin ? '#243d6e' : '#3D6FD4';
    const accentBg = isSuperAdmin ? '#F0F2F7' : '#F5F8FF';

    return (
        <div className="rappo-sign-in" style={{
            width: '100%',
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            background: '#fff',
            fontFamily: "'Pretendard','Apple SD Gothic Neo',-apple-system,sans-serif",
            boxSizing: 'border-box',
        }}>
            <style>{`
                .rappo-sign-in button,
                .rappo-sign-in input {
                    font-family: inherit;
                }
                .rappo-sign-in button:focus-visible,
                .rappo-sign-in a:focus-visible {
                    outline: 2px solid ${accent};
                    outline-offset: 4px;
                }
                .rappo-sign-in-brand a:focus-visible {
                    outline-color: #fff;
                }
                .rappo-sign-in input:focus-visible {
                    box-shadow: 0 0 0 3px ${accentBg};
                }
                .rappo-sign-in-layout {
                    grid-template-columns: minmax(0, clamp(46%, calc(60% - 200px), 50%)) minmax(0, 1fr);
                    min-height: 100vh;
                    min-height: 100svh;
                }
                .rappo-sign-in-brand {
                    position: relative;
                    isolation: isolate;
                    overflow: hidden;
                    align-items: center;
                    justify-content: center;
                    padding: clamp(48px, 6vh, 80px) clamp(32px, 4vw, 80px);
                }
                .rappo-sign-in-brand::before,
                .rappo-sign-in-brand::after {
                    content: '';
                    position: absolute;
                    z-index: -1;
                    width: clamp(480px, 48vw, 1040px);
                    aspect-ratio: 1;
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    border-radius: 50%;
                    pointer-events: none;
                }
                .rappo-sign-in-brand::before {
                    top: -48%;
                    right: -56%;
                    background: rgba(255, 255, 255, 0.02);
                }
                .rappo-sign-in-brand::after {
                    bottom: -64%;
                    left: -56%;
                }
                .rappo-sign-in-brand-inner {
                    width: 100%;
                    max-width: 500px;
                    min-width: 0;
                }
                .rappo-sign-in-logo {
                    display: block;
                    width: 80px;
                    margin-bottom: 32px;
                }
                .rappo-sign-in-brand-copy {
                    width: 100%;
                    min-width: 0;
                }
                .rappo-sign-in-brand-title {
                    display: block;
                    margin: 0 0 20px;
                    font-size: clamp(34px, 2.9vw, 44px);
                }
                .rappo-sign-in-brand-description {
                    max-width: 480px;
                    font-size: 15px;
                    color: #DAE5FC;
                }
                .rappo-sign-in-features {
                    display: grid;
                    grid-template-columns: repeat(2, minmax(0, 1fr));
                    gap: 24px 24px;
                    margin: 30px 0 0;
                }
                .rappo-sign-in-feature {
                    padding-top: 16px;
                    border-top: 1px solid rgba(255, 255, 255, 0.18);
                    min-width: 0;
                }
                .rappo-sign-in-service {
                    margin-top: 30px;
                    padding: 12px 18px;
                    background: #fff;
                }
                .rappo-sign-in-service:hover {
                    background: ${accentBg};
                }
                .rappo-sign-in-preview {
                    display: none;
                    min-width: 0;
                }
                .rappo-sign-in-login {
                    padding: clamp(48px, 6vh, 80px) clamp(32px, 4vw, 80px);
                }
                .rappo-sign-in-form {
                    width: 100%;
                    max-width: clamp(400px, 30vw, 440px);
                    min-width: 0;
                }
                .rappo-sign-in-login-title {
                    font-size: clamp(28px, 1.8vw, 30px);
                }
                @media (min-width: 1800px) {
                    .rappo-sign-in-brand {
                        padding-inline: clamp(48px, 3.3vw, 80px);
                    }
                    .rappo-sign-in-brand-inner {
                        display: grid;
                        grid-template-columns: minmax(0, 1fr) 280px;
                        align-items: center;
                        gap: 48px;
                        max-width: 920px;
                    }
                    .rappo-sign-in-preview {
                        display: block;
                    }
                }
                @media (min-width: 1920px) {
                    .rappo-sign-in-brand-inner {
                        grid-template-columns: minmax(0, 1fr) 320px;
                    }
                }
                @media (max-width: 1023px) {
                    .rappo-sign-in-layout {
                        grid-template-columns: minmax(0, 1fr);
                        grid-template-rows: auto 1fr;
                    }
                    .rappo-sign-in-brand {
                        align-items: flex-start;
                        padding: 28px max(24px, calc((100% - 400px) / 2));
                    }
                    .rappo-sign-in-brand::before,
                    .rappo-sign-in-brand::after {
                        display: none;
                    }
                    .rappo-sign-in-logo {
                        width: 64px;
                        margin-bottom: 24px;
                    }
                    .rappo-sign-in-brand-inner {
                        max-width: 400px;
                    }
                    .rappo-sign-in-brand-title {
                        font-size: clamp(28px, 5vw, 34px);
                        margin-bottom: 14px;
                    }
                    .rappo-sign-in-brand-description {
                        font-size: 14px;
                    }
                    .rappo-sign-in-features {
                        display: none;
                    }
                    .rappo-sign-in-service {
                        margin-top: 20px;
                        padding: 10px 16px;
                    }
                    .rappo-sign-in-login {
                        padding: 40px 24px 48px;
                    }
                    .rappo-sign-in-form {
                        max-width: 400px;
                    }
                    .rappo-sign-in-login-title {
                        font-size: 32px;
                    }
                }
                @media (prefers-reduced-motion: reduce) {
                    .rappo-sign-in *,
                    .rappo-sign-in *::before,
                    .rappo-sign-in *::after {
                        animation: none !important;
                        transition: none !important;
                    }
                }
            `}</style>

            <main className="rappo-sign-in-layout" style={{ display: 'grid', flex: 1, minWidth: 0 }}>
                {/* 브랜드 소개 — 검색용 문구와 서비스 링크는 모든 화면에서 노출 */}
                <section className="rappo-sign-in-brand" aria-label="라포 서비스 소개" style={{
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                    backgroundColor: isSuperAdmin ? accent : '#315FC0',
                    backgroundImage: 'radial-gradient(ellipse at 0% 0%, rgba(255, 255, 255, 0.08), transparent 65%), linear-gradient(155deg, transparent 25%, rgba(12, 34, 83, 0.2))',
                    color: '#fff',
                    transition: 'background-color 0.2s',
                }}>
                    <div className="rappo-sign-in-brand-inner">
                        <div className="rappo-sign-in-brand-copy">
                            <img className="rappo-sign-in-logo" src={I_WHITE_LOGO} alt="Rappo" style={{ height: 'auto' }} />
                            <h1 className="rappo-sign-in-brand-title" style={{
                                fontWeight: 700,
                                letterSpacing: '-1.6px',
                                lineHeight: 1.25,
                                wordBreak: 'keep-all',
                            }}>
                                회원과의 라포를 넘어,{' '}<br />
                                센터의 내일까지
                            </h1>
                            <p className="rappo-sign-in-brand-description" style={{ margin: 0, fontWeight: 500, lineHeight: 1.6, wordBreak: 'keep-all' }}>
                                헬스장·PT샵 회원관리 프로그램 라포
                            </p>
                            <ul className="rappo-sign-in-features" style={{
                                padding: 0,
                                listStyle: 'none',
                                lineHeight: 1.5,
                                color: '#fff',
                            }}>
                                {[
                                    { title: '손실 예측', description: '재등록이 흔들리는 회원을 먼저 알려드려요', icon: (
                                        <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none">
                                            <path d="M12 3 3.5 7v5c0 4.5 8.5 9 8.5 9s8.5-4.5 8.5-9V7L12 3Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
                                            <path d="M12 8v5m0 3h.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                                        </svg>
                                    ) },
                                    { title: '매출 예측·분석', description: '들어올 매출과 흐름을 한눈에', icon: (
                                        <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none">
                                            <path d="M4 4v16h16M8 15l4-5 4 2 4-6m-5 0h5v5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    ) },
                                    { title: '진짜 회원 목소리', description: '회원이 남긴 솔직한 평가를 그대로', icon: (
                                        <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none">
                                            <path d="M20 11.5a8 8 0 0 1-8 8H5l-3 2v-10a10 10 0 0 1 18 0Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
                                            <path d="M7 10h8m-8 4h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                        </svg>
                                    ) },
                                    { title: '센터 리포트', description: '매일 아침 센터 현황을 정리해 드려요', icon: (
                                        <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none">
                                            <path d="M14 3H5v18h14V8l-5-5Zm0 0v5h5M8 12h8m-8 4h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    ) },
                                ].map((feature) => (
                                    <li key={feature.title} className="rappo-sign-in-feature">
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <span style={{ display: 'flex', flexShrink: 0, color: '#C8DAFF' }}>{feature.icon}</span>
                                            <strong style={{ fontSize: 15, fontWeight: 600, letterSpacing: '-0.3px' }}>{feature.title}</strong>
                                        </div>
                                        <p style={{ margin: '8px 0 0', fontSize: 13, lineHeight: 1.7, color: '#DAE5FC', wordBreak: 'keep-all' }}>{feature.description}</p>
                                    </li>
                                ))}
                            </ul>
                            <a
                                className="rappo-sign-in-service"
                                href="https://www.rappomanager.info/"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    display: 'inline-block',
                                    borderRadius: 9,
                                    fontSize: 14,
                                    fontWeight: 600,
                                    lineHeight: 1.6,
                                    color: isSuperAdmin ? accent : '#315FC0',
                                    textDecoration: 'none',
                                    transition: 'background-color 0.2s',
                                }}
                            >
                                서비스 소개 →
                            </a>
                        </div>
                        {/* 장식용 제품 미리보기 — 예시 데이터는 접근성 트리와 검색 스니펫에서 제외 */}
                        <div className="rappo-sign-in-preview" aria-hidden="true" data-nosnippet="" style={{ position: 'relative', padding: '24px 6px', pointerEvents: 'none' }}>
                            <div style={{ position: 'absolute', inset: '34px 0 50px 16px', borderRadius: 20, background: 'rgba(255, 255, 255, 0.12)', border: '1px solid rgba(255, 255, 255, 0.22)', transform: 'rotate(5deg)' }} />
                            <div style={{ position: 'relative', padding: 22, borderRadius: 18, background: '#fff', color: '#24324B', border: '1px solid rgba(255, 255, 255, 0.7)', boxShadow: '0 12px 32px rgba(12, 34, 83, 0.12)', transform: 'rotate(-3deg)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                                    <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '-0.3px' }}>센터 운영 미리보기</span>
                                    <span style={{ fontSize: 10, lineHeight: 1.5, padding: '2px 6px', borderRadius: 4, background: '#F2F5FA', color: '#788397' }}>예시</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 12px', borderRadius: 8, background: '#FFF8ED', marginBottom: 22 }}>
                                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#CE9850', flexShrink: 0 }} />
                                    <span style={{ fontSize: 12, fontWeight: 600, color: '#8B6534' }}>이탈 위험 회원 3명</span>
                                </div>
                                <p style={{ margin: '0 0 8px', fontSize: 12, lineHeight: 1.5, color: '#6C788D' }}>이번 달 예상 매출</p>
                                <p style={{ margin: 0, fontSize: 25, fontWeight: 700, letterSpacing: '-0.8px', lineHeight: 1.3, fontVariantNumeric: 'tabular-nums' }}>69,400,000<span style={{ fontSize: 13, fontWeight: 500, marginLeft: 3 }}>원</span></p>
                                <svg width="100%" height="90" viewBox="0 0 240 90" fill="none" style={{ display: 'block', margin: '14px 0 18px' }}>
                                    <path d="M0 18h240M0 48h240M0 78h240" stroke="#EDF1F7" strokeWidth="1" />
                                    <rect x="9" y="52" width="17" height="26" rx="3" fill="#E5EDFA" />
                                    <rect x="43" y="42" width="17" height="36" rx="3" fill="#E5EDFA" />
                                    <rect x="77" y="48" width="17" height="30" rx="3" fill="#E5EDFA" />
                                    <rect x="111" y="30" width="17" height="48" rx="3" fill="#E5EDFA" />
                                    <rect x="145" y="35" width="17" height="43" rx="3" fill="#E5EDFA" />
                                    <rect x="179" y="21" width="17" height="57" rx="3" fill={accent} opacity="0.35" />
                                    <rect x="213" y="27" width="17" height="51" rx="3" fill={accent} opacity="0.7" />
                                    <path d="m17 43 34-10 34 6 34-18 34 5 34-14 34 6" stroke={accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <circle cx="221" cy="18" r="3.5" fill={accent} stroke="#fff" strokeWidth="2" />
                                </svg>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 9, borderTop: '1px solid #EDF1F7', paddingTop: 15 }}>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, color: accent }}>
                                        <path d="M5 3h14v18H5zM8 8h8m-8 4h8m-8 4h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    <div style={{ fontSize: 11, lineHeight: 1.7 }}>
                                        <p style={{ margin: 0, fontWeight: 600 }}>오늘의 센터 리포트</p>
                                        <p style={{ margin: 0, color: '#788397' }}>회원 현황부터 매출 흐름까지 정리했어요</p>
                                    </div>
                                </div>
                            </div>
                            <div style={{ position: 'relative', margin: '-6px -6px 0 26px', padding: '16px 18px', borderRadius: 12, background: '#F5F8FF', color: '#24324B', border: '1px solid #fff', boxShadow: '0 8px 20px rgba(12, 34, 83, 0.08)', transform: 'rotate(2deg)' }}>
                                <p style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '0 0 7px', fontSize: 11, lineHeight: 1.4, color: '#6C788D' }}>
                                    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" style={{ color: accent }}>
                                        <path d="M17 9a7 7 0 0 1-7 7H5l-3 2V9a7.5 7.5 0 0 1 15 0Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
                                        <path d="M6 8h7M6 11h4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                                    </svg>
                                    회원 목소리
                                </p>
                                <p style={{ margin: 0, fontSize: 14, fontWeight: 600, lineHeight: 1.5, letterSpacing: '-0.3px' }}>“수업이 체계적이에요”</p>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="rappo-sign-in-login" aria-labelledby="rappo-login-title" style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 0,
                }}>
                    <div className="rappo-sign-in-form">
                        <h2 className="rappo-sign-in-login-title" id="rappo-login-title" style={{
                            margin: '0 0 10px',
                            fontWeight: 700,
                            letterSpacing: '-0.8px',
                            lineHeight: 1.3,
                            color: '#192638',
                        }}>
                            로그인
                        </h2>
                        <p style={{ margin: '0 0 32px', fontSize: 15, lineHeight: 1.6, color: '#6B7280' }}>
                            {isSuperAdmin ? '프랜차이즈 통합 관리를 시작해요' : '오늘의 센터 관리를 시작해요'}
                        </p>

                        {/* 탭 전환 */}
                        <div role="group" aria-label="로그인 계정 유형" style={{
                            display: 'flex',
                            background: '#F3F5F8',
                            borderRadius: 12,
                            padding: 4,
                            marginBottom: 32,
                        }}>
                            {[
                                { key: false, label: '센터 관리자', icon: (
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M3 2h10a1 1 0 011 1v11H2V3a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.4" fill="none"/>
                                        <rect x="5" y="5" width="2" height="2" rx="0.3" fill="currentColor"/>
                                        <rect x="9" y="5" width="2" height="2" rx="0.3" fill="currentColor"/>
                                        <rect x="5" y="9" width="2" height="2" rx="0.3" fill="currentColor"/>
                                        <rect x="9" y="9" width="2" height="2" rx="0.3" fill="currentColor"/>
                                    </svg>
                                ) },
                                { key: true, label: '슈퍼 관리자', icon: (
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <circle cx="8" cy="5.5" r="3" stroke="currentColor" strokeWidth="1.4" fill="none"/>
                                        <path d="M3 14c0-2.8 2.2-5 5-5s5 2.2 5 5" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round"/>
                                    </svg>
                                ) },
                            ].map((tab) => {
                                const active = isSuperAdmin === tab.key;
                                return (
                                    <button
                                        key={String(tab.key)}
                                        type="button"
                                        aria-pressed={active}
                                        onClick={() => setIsSuperAdmin(tab.key)}
                                        style={{
                                            flex: 1,
                                            padding: '12px 0',
                                            borderRadius: 9,
                                            border: 'none',
                                            cursor: 'pointer',
                                            fontSize: 14,
                                            fontWeight: 600,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 6,
                                            background: active ? '#fff' : 'transparent',
                                            color: active ? (tab.key ? '#1A305D' : '#4A7FE5') : '#6B7280',
                                            boxShadow: 'none',
                                            transition: 'all 0.2s',
                                        }}
                                    >
                                        {tab.icon}
                                        {tab.label}
                                    </button>
                                );
                            })}
                        </div>

                        {/* 슈퍼관리자 안내 */}
                        {isSuperAdmin && (
                            <div style={{
                                marginBottom: 20,
                                padding: '10px 14px',
                                background: accentBg,
                                borderRadius: 8,
                                lineHeight: 1.6,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                            }}>
                                <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
                                    <circle cx="8" cy="8" r="7" stroke={accent} strokeWidth="1.4" fill="none"/>
                                    <path d="M8 5v3M8 10v0.5" stroke={accent} strokeWidth="1.6" strokeLinecap="round"/>
                                </svg>
                                <span style={{ fontSize: 13, color: '#1A305D', fontWeight: 500 }}>
                                    프랜차이즈 대표 · 다지점 관리자 전용 계정입니다
                                </span>
                            </div>
                        )}

                        {/* 아이디 입력 */}
                        <div style={{ marginBottom: 20 }}>
                            <label htmlFor="rappo-login-id" style={{ fontSize: 14, fontWeight: 600, color: '#353535', marginBottom: 8, display: 'block' }}>
                                아이디
                            </label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    id="rappo-login-id"
                                    type="text"
                                    name={isSuperAdmin ? 'superadmin-id' : 'center-id'}
                                    autoComplete={isSuperAdmin ? 'section-superadmin username' : 'section-center username'}
                                    placeholder={isSuperAdmin ? '슈퍼 관리자 아이디' : '센터 관리자 아이디'}
                                    value={userId}
                                    maxLength={25}
                                    onChange={(e) => setUserId(e.target.value.trim())}
                                    style={{
                                        width: '100%',
                                        padding: '15px 56px 15px 16px',
                                        borderRadius: 10,
                                        border: '1px solid #DDE2ED',
                                        fontSize: 15,
                                        color: '#353535',
                                        outline: 'none',
                                        background: '#fff',
                                        boxSizing: 'border-box',
                                        transition: 'border-color 0.2s',
                                    }}
                                    onFocus={(e) => e.currentTarget.style.borderColor = accent}
                                    onBlur={(e) => e.currentTarget.style.borderColor = '#DDE2ED'}
                                />
                                {userId.length > 0 && (
                                    <button
                                        type="button"
                                        aria-label="아이디 지우기"
                                        // 탭 한 번에 아이디 → 비밀번호로 바로 가도록 탭 순서에서 뺀다 (마우스 클릭은 그대로)
                                        tabIndex={-1}
                                        onClick={() => setUserId('')}
                                        style={{
                                            position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                                            background: 'none', border: 'none', cursor: 'pointer', padding: 4,
                                            color: '#6B7280', fontSize: 16, lineHeight: 1,
                                        }}
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* 비밀번호 입력 */}
                        <div style={{ marginBottom: 28 }}>
                            <label htmlFor="rappo-login-password" style={{ fontSize: 14, fontWeight: 600, color: '#353535', marginBottom: 8, display: 'block' }}>
                                비밀번호
                            </label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    id="rappo-login-password"
                                    type={showPassword ? 'text' : 'password'}
                                    name={isSuperAdmin ? 'superadmin-pw' : 'center-pw'}
                                    autoComplete={isSuperAdmin ? 'section-superadmin current-password' : 'section-center current-password'}
                                    placeholder="비밀번호를 입력해주세요"
                                    value={userPassword}
                                    maxLength={25}
                                    onChange={(e) => setUserPassword(e.target.value.trim())}
                                    onKeyDown={handleKeyPress}
                                    style={{
                                        width: '100%',
                                        padding: '15px 56px 15px 16px',
                                        borderRadius: 10,
                                        border: '1px solid #DDE2ED',
                                        fontSize: 15,
                                        color: '#353535',
                                        outline: 'none',
                                        background: '#fff',
                                        boxSizing: 'border-box',
                                        transition: 'border-color 0.2s',
                                    }}
                                    onFocus={(e) => e.currentTarget.style.borderColor = accent}
                                    onBlur={(e) => e.currentTarget.style.borderColor = '#DDE2ED'}
                                />
                                {userPassword.length > 0 && (
                                    <button
                                        type="button"
                                        aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
                                        aria-pressed={showPassword}
                                        // 비밀번호 → 로그인 버튼도 탭 한 번에 가도록 같은 처리
                                        tabIndex={-1}
                                        onClick={() => setShowPassword(!showPassword)}
                                        style={{
                                            position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                                            background: 'none', border: 'none', cursor: 'pointer', padding: 4,
                                            color: '#6B7280', fontSize: 14,
                                        }}
                                    >
                                        {showPassword ? '숨김' : '보기'}
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* 로그인 버튼 */}
                        <button
                            type="button"
                            onClick={handleLogin}
                            disabled={isDisabled}
                            style={{
                                width: '100%',
                                padding: '16px 0',
                                borderRadius: 10,
                                border: 'none',
                                cursor: isDisabled ? 'default' : 'pointer',
                                fontSize: 16,
                                fontWeight: 700,
                                color: '#fff',
                                background: isDisabled ? '#C4CDD9' : accent,
                                transition: 'background 0.2s',
                                marginBottom: 24,
                            }}
                            onMouseEnter={(e) => { if (!isDisabled) e.currentTarget.style.background = accentHover; }}
                            onMouseLeave={(e) => { if (!isDisabled) e.currentTarget.style.background = accent; }}
                        >
                            {isLoading ? '로그인 중...' : '로그인'}
                        </button>

                        {/* 하단 링크 */}
                        {isSuperAdmin ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-start', alignItems: 'center', gap: 8, lineHeight: 1.6, marginBottom: 28 }}>
                                <span style={{ fontSize: 13, color: '#6B7280' }}>계정이 없으신가요?</span>
                                <button
                                    type="button"
                                    onClick={() => window.open('http://pf.kakao.com/_ICxhcb/chat', '_blank')}
                                    style={{ padding: 0, border: 'none', background: 'none', fontSize: 13, color: '#1A305D', fontWeight: 600, cursor: 'pointer' }}
                                >
                                    카카오 채널 문의
                                </button>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', flexWrap: 'wrap', gap: 8, lineHeight: 1.6, marginBottom: 28 }}>
                                <span style={{ fontSize: 13, color: '#6B7280' }}>아직 계정이 없으신가요?</span>
                                <button
                                    type="button"
                                    onClick={() => navigate('/signUp')}
                                    style={{ padding: 0, border: 'none', background: 'none', fontSize: 13, color: '#4A7FE5', fontWeight: 600, cursor: 'pointer' }}
                                >
                                    회원가입
                                </button>
                            </div>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', flexWrap: 'wrap', gap: 16, lineHeight: 1.6 }}>
                            <a
                                href="https://www.rappomanager.info/"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ fontSize: 13, color: '#6B7280', textDecoration: 'none' }}
                            >
                                라포 홈페이지
                            </a>
                            <span style={{ width: 1, height: 12, background: '#ddd' }} />
                            <button
                                type="button"
                                onClick={() => window.open('http://pf.kakao.com/_ICxhcb', '_blank')}
                                style={{ padding: 0, border: 'none', background: 'none', fontSize: 13, color: '#6B7280', cursor: 'pointer' }}
                            >
                                1:1 카카오 상담
                            </button>
                        </div>
                    </div>
                </section>
            </main>

            {/* 사업자 정보 (카드사 심사용) */}
            <footer style={{ width: '100%', padding: '24px 24px 28px', boxSizing: 'border-box', background: '#F8FAFC', wordBreak: 'keep-all', overflowWrap: 'anywhere' }}>
                <BusinessInfoFooter variant="inline" style={{ padding: 0 }} />
            </footer>
        </div>
    );
};
export default SignInPage;
